/**
 * Estilos de Gavi, su bocadillo, el lema, los destellos y el pulso de «Auto»
 * (copia de la maqueta `primera-travesia.html`). Todo queda quieto con
 * «reducir movimiento» (clase `quieta` y la media query).
 */
import { css, keyframes } from '@emotion/css';

import { color, movimiento, radio, sombra } from '#components/mobile/ui/tokens';

/**
 * Colores del dibujo de Gavi. Son de la ilustración (iguales en claro y
 * oscuro salvo el borde), con variables `--p-gavi-*` por si un tema quiere
 * cambiarlos.
 */
export const colorGavi = {
  blanco: 'var(--p-gavi-blanco, #ffffff)',
  gris: 'var(--p-gavi-gris, #9fadc2)',
  /** Contorno: visible en claro; en oscuro el blanco ya contrasta. */
  borde: `var(--p-gavi-borde, ${color.line2})`,
  pico: 'var(--p-gavi-pico, #e8a93b)',
  ojo: '#1d2733',
  brillo: '#ffffff',
  moflete: '#d9534f',
  punta: '#59657a',
} as const;

const balanceo = keyframes({
  '50%': { transform: 'translateY(-2px) rotate(-2deg)' },
});
const parpadeo = keyframes({
  '0%, 92%, 100%': { transform: 'scaleY(1)' },
  '95%': { transform: 'scaleY(.1)' },
});
const aleteo = keyframes({
  from: { transform: 'rotate(0)' },
  to: { transform: 'rotate(-38deg)' },
});
const llegar = keyframes({
  from: { transform: 'translate(140px, -70px) scale(.7)', opacity: 0 },
  '60%': { opacity: 1 },
});
const salto = keyframes({ '50%': { transform: 'translateY(-9px)' } });
const temblor = keyframes({ '50%': { transform: 'translateX(1.5px)' } });
const aparecer = keyframes({ from: { opacity: 0 } });
const lemaEntra = keyframes({
  from: { opacity: 0, transform: 'translateX(-14px) scale(.9)' },
});
const destello = keyframes({
  from: { transform: 'translate(0,0) rotate(45deg) scale(.4)', opacity: 1 },
  to: {
    transform: 'translate(var(--dx), var(--dy)) rotate(45deg) scale(1)',
    opacity: 0,
  },
});
const latido = keyframes({
  '50%': { boxShadow: `0 0 0 5px ${color.accentSoft}` },
});
const saltito = keyframes({
  '40%': { transform: 'translateY(-5px) scale(1.08)' },
});

const sinMovimiento = {
  '&, & *': {
    animation: 'none !important',
    transition: 'none !important',
  },
} as const;

/** Gavi (`Gavi.tsx`): 56 × 52 px y los gestos por clase de estado. */
export const claseGavi = css({
  display: 'block',
  width: 56,
  height: 52,
  '& svg': { width: '100%', height: '100%', overflow: 'visible' },
  '& .cuerpo-g': {
    transformOrigin: '30px 50px',
    animation: `${balanceo} 2.4s ease-in-out infinite`,
  },
  '& .ojo': {
    transformOrigin: '45px 21px',
    animation: `${parpadeo} 5s infinite`,
  },
  '& .ala': {
    transformOrigin: '26px 34px',
    transition: `transform .3s ${movimiento.muelle}`,
  },
  '& .cabeza': {
    transformOrigin: '40px 28px',
    transition: `transform .3s ${movimiento.muelle}`,
  },
  '& .plumas': { opacity: 0, transition: 'opacity .2s' },
  '&.vuela': { animation: `${llegar} .7s ${movimiento.muelle}` },
  '&.vuela .ala': { animation: `${aleteo} .16s 4 alternate` },
  '&.ladea .cabeza': { transform: 'rotate(-14deg)' },
  '&.aplaude .ala': { animation: `${aleteo} .14s 6 alternate` },
  '&.aplaude .cuerpo-g': { animation: `${salto} .5s 2` },
  '&.tapa .ala': { transform: 'rotate(-112deg) translate(-2px, -8px)' },
  '&.tapa .cuerpo-g': { animation: `${temblor} .12s 6` },
  '&.platos .ojo': { transform: 'scale(1.9)', animation: 'none' },
  '&.eriza .plumas': { opacity: 1 },
  '&.eriza .cuerpo-g': { animation: `${temblor} .1s 8` },
  // Quieta: sin balanceo ni parpadeo; los gestos fijos (cabeza ladeada,
  // ala sobre los ojos, ojos-plato, plumas) siguen viéndose, sin transición.
  '&.quieta': sinMovimiento,
  '@media (prefers-reduced-motion: reduce)': sinMovimiento,
});

/** Contenedor de Gavi + bocadillo: Gavi se posa encima, a la izquierda. */
export const claseBocWrap = css({
  position: 'relative',
  paddingTop: 30,
  '& > .gavi-pos': {
    position: 'absolute',
    left: 6,
    top: -10,
    zIndex: 2,
  },
});

/** Bocadillo: tarjeta con colita, como «Consejos». */
export const claseBocadillo = css({
  position: 'relative',
  backgroundColor: color.surface,
  border: `1px solid ${color.line}`,
  borderRadius: radio.tarjeta,
  padding: '12px 14px',
  boxShadow: sombra.tarjeta,
  fontSize: 14,
  lineHeight: 1.38,
  fontWeight: 600,
  color: color.fg,
  minHeight: 50,
  boxSizing: 'border-box',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 30,
    top: -7,
    width: 12,
    height: 12,
    background: 'inherit',
    borderLeft: `1px solid ${color.line}`,
    borderTop: `1px solid ${color.line}`,
    transform: 'rotate(45deg)',
  },
  '& .acciones': { marginTop: 10 },
  '& .texto': { display: 'block' },
  '& .texto.cambia': { animation: `${aparecer} .3s` },
  // Dentro de una hoja: sobre surface-2 y sin sombra.
  '&.en-hoja': { backgroundColor: color.surface2, boxShadow: 'none' },
  // En el Astillero (escena de noche, siempre oscura como el resto de la
  // escena de `Astillero.tsx`).
  '&.astillero': {
    backgroundColor: '#13233a',
    borderColor: '#263b58',
    color: '#eef1f8',
  },
  '&.astillero::before': { borderColor: '#263b58' },
  '@media (prefers-reduced-motion: reduce)': {
    '& .texto.cambia': { animation: 'none' },
  },
});

/** Lema «Tu dinero. Tus decisiones. Tu rumbo.», frase a frase. */
export const claseLema = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  margin: '8px 0 6px',
  '& > span': {
    fontWeight: 800,
    fontSize: 19,
    lineHeight: 1.2,
    color: color.fg,
    animation: `${lemaEntra} .5s cubic-bezier(.3,1.5,.5,1) backwards`,
  },
  '& > span.fin': { color: color.accent, fontSize: 22 },
  '&.quieto > span': { animation: 'none' },
  '@media (prefers-reduced-motion: reduce)': {
    '& > span': { animation: 'none' },
  },
});

/** Pie bajo el lema (p. ej. «Y lo que gastes, apúntalo con la brújula…»). */
export const claseLemaPie = css({
  margin: '4px 0 8px',
  fontSize: 13.5,
  color: color.fg2,
});

/** Destellos: rombos que salen hacia los cuatro puntos cardinales. */
export const claseDestellos = css({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  '& > i': {
    position: 'absolute',
    left: '50%',
    top: '50%',
    width: 8,
    height: 8,
    margin: -4,
    background: color.accent2,
    borderRadius: 2,
    transform: 'rotate(45deg)',
    animation: `${destello} .9s ease-out forwards`,
  },
});

/** Pulso del botón «Auto» del teclado cuando Gavi lo propone. */
export const clasePulso = css({
  boxShadow: `0 0 0 2px ${color.accent}`,
  animation: `${latido} 1.2s infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

/** Saltito de la caja de emoji al marcar una categoría. */
export const claseSaltito = css({
  animation: `${saltito} .35s ${movimiento.muelle}`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});
